import React, { useState } from 'react';
import { PrismLoading, PrismButton } from '@prism-ui/react';
import { toast } from '../../../Toaster/toast';
import type { EmailHealthResponse, EmailHealthByTypeWindows } from '../../../../types/emailHealth';
import { buildMarkdownReport } from '../../../../utils/buildMarkdownReport';

interface EmailHealthWidgetProps {
  health: EmailHealthResponse | null;
  loading: boolean;
  error: string | null;
  expanded: boolean;
  onToggleExpand: () => void;
  onRefresh: () => void;
}

const STATUS_CONFIG = {
  healthy:  { icon: '✅', label: 'Healthy',  cls: 'email-health-status--healthy'  },
  degraded: { icon: '⚠️', label: 'Degraded', cls: 'email-health-status--degraded' },
  down:     { icon: '🔴', label: 'Down',     cls: 'email-health-status--down'     },
} as const;

const EmailHealthWidget: React.FC<EmailHealthWidgetProps> = ({
  health,
  loading,
  error,
  expanded,
  onToggleExpand,
  onRefresh,
}) => {
  const [clipboardFallback, setClipboardFallback] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [byTypeWindow, setByTypeWindow] = useState<keyof EmailHealthByTypeWindows>('24h');

  const handleCopyToCopilot = async () => {
    if (!health) return;
    const md = buildMarkdownReport(health);
    try {
      await navigator.clipboard.writeText(md);
      setCopied(true);
      toast.success('Diagnostic copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setClipboardFallback(md);
    }
  };

  if (loading && !health) {
    return (
      <div className="email-health-widget">
        <PrismLoading as="spinner" container="inline" />
      </div>
    );
  }

  if (error && !health) {
    return (
      <div className="email-health-widget email-health-widget--error">
        <span>⚠️ Failed to load email health: {error}</span>
        <PrismButton size="sm" display="outline" tone="info" className="clr:ml-2" onPrismClick={onRefresh}>
          Retry
        </PrismButton>
      </div>
    );
  }

  const statusCfg = health ? STATUS_CONFIG[health.status] : null;
  const isUnhealthy = health && health.status !== 'healthy';

  const degradedMessage = health && isUnhealthy
    ? !health.smtp.reachable
      ? `SMTP unreachable: ${health.smtp.error ?? 'unknown error'}`
      : health.rolling24h.failureRate > 0
      ? `${(health.rolling24h.failureRate * 100).toFixed(1)}% failure rate in last 24h`
      : health.alarmState === 'ALARM'
      ? 'CloudWatch alarm is firing'
      : (health.scheduled?.overdueCount ?? 0) > 0
      ? `${health.scheduled.overdueCount} scheduled reminder${health.scheduled.overdueCount > 1 ? 's' : ''} overdue`
      : 'One or more reminder jobs have failures'
    : null;

  return (
    <div className="email-health-widget">
      {/* Hero status + at-a-glance stats */}
      <div className="email-health-header">
        <div className="email-health-hero">
          {statusCfg ? (
            <div className={`email-health-hero-badge ${statusCfg.cls}`}>
              <span className="email-health-hero-icon" aria-hidden="true">{statusCfg.icon}</span>
              <span className="email-health-hero-label">{statusCfg.label}</span>
            </div>
          ) : (
            <div className="email-health-hero-badge">
              <PrismLoading as="spinner" container="inline" />
            </div>
          )}
          {degradedMessage && (
            <p className="email-health-degraded-msg">{degradedMessage}</p>
          )}
        </div>

        {health && (
          <div className="email-health-stats">
            <div className="email-health-stat">
              <span className="email-health-stat-value">{health.rolling24h.sent}</span>
              <span className="email-health-stat-label">Sent (24h)</span>
            </div>
            <div className="email-health-stat">
              <span className={`email-health-stat-value${health.rolling24h.failed > 0 ? ' email-health-stat-value--bad' : ''}`}>
                {health.rolling24h.failed}
              </span>
              <span className="email-health-stat-label">Failed (24h)</span>
            </div>
            <div className="email-health-stat">
              <span className={`email-health-stat-value${health.rolling24h.failureRate > 0 ? ' email-health-stat-value--bad' : ''}`}>
                {(health.rolling24h.failureRate * 100).toFixed(1)}%
              </span>
              <span className="email-health-stat-label">Failure Rate</span>
            </div>
            <div className="email-health-stat">
              <span className="email-health-stat-value">
                {health.smtp.reachable ? '✅' : '🔴'}
              </span>
              <span className="email-health-stat-label">SMTP</span>
            </div>
            {health.scheduled && (
              <div className="email-health-stat">
                <span className={`email-health-stat-value${health.scheduled.overdueCount > 0 ? ' email-health-stat-value--bad' : ''}`}>
                  {health.scheduled.overdueCount > 0
                    ? health.scheduled.overdueCount
                    : health.scheduled.pendingCount}
                </span>
                <span className="email-health-stat-label">
                  {health.scheduled.overdueCount > 0 ? 'Overdue' : 'Pending'}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Action bar */}
      <div className="email-health-actions">
        <PrismButton size="sm" display="link" tone="info" onPrismClick={onToggleExpand}>
          <span>{expanded ? '▲' : '▼'}</span> {expanded ? 'Hide details' : 'Show details'}
        </PrismButton>
        <div className="email-health-actions-right">
          {isUnhealthy && health && (
            <PrismButton
              size="sm"
              display="outline"
              tone="info"
              onPrismClick={handleCopyToCopilot}
              title="Copy diagnostic report to clipboard for Copilot"
              aria-label="Copy to Copilot"
            >
              📋 {copied ? 'Copied!' : 'Copy to Copilot'}
            </PrismButton>
          )}
          <PrismButton
            size="sm"
            display="outline"
            tone="info"
            onPrismClick={onRefresh}
            disabled={loading}
            loading={loading}
            title="Refresh email health status"
          >
            ↻ Refresh
          </PrismButton>
        </div>
      </div>

      {/* Expanded detail panel */}
      {expanded && health && (
        <div className="email-health-details">
          <table className="email-health-table">
            <tbody>
              <tr>
                <td className="email-health-label">SMTP</td>
                <td>
                  {health.smtp.reachable
                    ? '✅ Reachable'
                    : `🔴 Unreachable — ${health.smtp.error ?? ''}`}
                </td>
              </tr>
              <tr>
                <td className="email-health-label">Last Sent</td>
                <td>
                  {health.rolling24h.lastSentAt
                    ? new Date(health.rolling24h.lastSentAt).toLocaleString()
                    : 'No emails in 24h'}
                </td>
              </tr>
              <tr>
                <td className="email-health-label">Alarm State</td>
                <td>{health.alarmState ?? 'Unknown'}</td>
              </tr>
              {health.scheduled && (
                <>
                  <tr>
                    <td className="email-health-label">Overdue</td>
                    <td className={health.scheduled.overdueCount > 0 ? 'email-health-stat-value--bad' : ''}>
                      {health.scheduled.overdueCount === 0
                        ? 'None'
                        : <><strong>{health.scheduled.overdueCount}</strong> reminder{health.scheduled.overdueCount > 1 ? 's' : ''} past send time — check <a href="/admin-settings/email-reminders" target="_blank" rel="noreferrer">Email Reminders ↗</a></>}
                    </td>
                  </tr>
                  <tr>
                    <td className="email-health-label">Pending</td>
                    <td>{health.scheduled.pendingCount}</td>
                  </tr>
                  <tr>
                    <td className="email-health-label">Next Send</td>
                    <td>
                      {health.scheduled.nextScheduledAt
                        ? new Date(health.scheduled.nextScheduledAt).toLocaleString()
                        : 'None scheduled'}
                    </td>
                  </tr>
                  {(health.scheduled.activeRecurringCount ?? 0) > 0 && (
                    <tr>
                      <td className="email-health-label">Recurring</td>
                      <td>
                        {health.scheduled.activeRecurringCount} active series
                        {health.scheduled.recurringLastSentAt
                          ? <> — last fired {new Date(health.scheduled.recurringLastSentAt).toLocaleString()}</>
                          : <span className="email-health-degraded-msg"> — never fired</span>}
                      </td>
                    </tr>
                  )}
                </>
              )}
            </tbody>
          </table>

          {health.byTypeWindows && (
            <>
              <div className="email-health-bytype-header">
                <h6 className="email-health-subheading">By Email Type</h6>
                <div className="email-health-bytype-tabs">
                  {(['24h', '7d', '30d', 'ytd'] as const).map(w => (
                    <button
                      key={w}
                      className={`email-health-bytype-tab${byTypeWindow === w ? ' email-health-bytype-tab--active' : ''}`}
                      onClick={() => setByTypeWindow(w)}
                    >
                      {w === '24h' ? '24h' : w === '7d' ? '7 days' : w === '30d' ? '30 days' : 'YTD'}
                    </button>
                  ))}
                </div>
              </div>
              {health.byTypeWindows[byTypeWindow].length > 0 ? (
                <table className="email-health-table">
                  <thead>
                    <tr><th>Type</th><th>Sent</th></tr>
                  </thead>
                  <tbody>
                    {health.byTypeWindows[byTypeWindow].map(t => (
                      <tr key={t.emailType}>
                        <td>{t.emailType}</td>
                        <td>{t.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="email-health-empty">No emails recorded in this window.</p>
              )}
            </>
          )}

          {!health.byTypeWindows && health.byType.length > 0 && (
            <>
              <h6 className="email-health-subheading">By Email Type (24h)</h6>
              <table className="email-health-table">
                <thead>
                  <tr><th>Type</th><th>Sent</th></tr>
                </thead>
                <tbody>
                  {health.byType.map(t => (
                    <tr key={t.emailType}>
                      <td>{t.emailType}</td>
                      <td>{t.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {health.reminderConfigs.length > 0 && (
            <>
              <h6 className="email-health-subheading">Reminder Config Last Runs</h6>
              <table className="email-health-table">
                <thead>
                  <tr>
                    <th>Feature</th>
                    <th>Chapter</th>
                    <th>Last Run</th>
                    <th>Sent</th>
                    <th>Failed</th>
                  </tr>
                </thead>
                <tbody>
                  {health.reminderConfigs.map((c, i) => (
                    <tr key={i} className={(c.lastRunFailedCount ?? 0) > 0 ? 'email-health-row--failed' : ''}>
                      <td>{c.feature}</td>
                      <td>{c.chapterName ?? c.chapterId}</td>
                      <td>{c.lastRunAt ? new Date(c.lastRunAt).toLocaleString() : 'Never'}</td>
                      <td>{c.lastRunSentCount ?? 0}</td>
                      <td>{c.lastRunFailedCount ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          <a
            href="https://console.aws.amazon.com/cloudwatch/home#dashboards:name=ClearPortal-Email"
            target="_blank"
            rel="noreferrer"
            className="email-health-cw-link"
          >
            View CloudWatch Dashboard ↗
          </a>
        </div>
      )}

      {/* Clipboard fallback modal (when navigator.clipboard is unavailable) */}
      {clipboardFallback && (
        <div className="email-health-clipboard-modal">
          <div
            className="email-health-clipboard-backdrop"
            onClick={() => setClipboardFallback(null)}
          />
          <div className="email-health-clipboard-dialog">
            <div className="email-health-clipboard-dialog-header">
              <strong>Copy Diagnostic Report</strong>
              <PrismButton
                size="sm"
                display="link"
                tone="info"
                aria-label="Close"
                onPrismClick={() => setClipboardFallback(null)}
              >
                ✕
              </PrismButton>
            </div>
            <p className="email-health-clipboard-hint">
              Clipboard access was denied. Select all and copy manually:
            </p>
            <pre className="email-health-clipboard-pre">{clipboardFallback}</pre>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailHealthWidget;
